import { computed, inject, Injectable, signal, untracked } from '@angular/core';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { LngLatBoundsLike } from 'maplibre-gl';

import { MapBaseSource, MapLayerSource } from '@connect-services/client/components/map/map.component';
import { LayerStyleTypeEnum } from '@connect-services/client/models/highlight-entity.model';
import { AssertService } from '@connect-services/client/services/assert.service';
import { CreateFieldInterventionService } from '@connect-services/client/services/create-field-intervention.service';
import { EntitiesTableByLayerService } from '@connect-services/client/services/entities-table-by-layer.service';
import { MapBaseService } from '@connect-services/client/services/map-base.service';
import { MapHighlightEntityService } from '@connect-services/client/services/map-highlight-entity.service';
import { SourcesService } from '@connect-services/client/services/sources.service';
import { Feature, FeatureCollection, Geometry, MultiPolygon, Polygon } from '@connect-services/src/sdk/api';

export enum EntityTypeEnum {
    POINT = 'Point',
    POLYGON = 'Polygon',
    LINE = 'LineString',
}

export type PendingEntityMapRequestBase = {
    entityId: string;
    geom: Geometry;
    geomBboxEntity: EntityLocation;
    layerId: string;
};

export type PendingCenterEntityRequest = PendingEntityMapRequestBase & {
    entityType: LayerStyleTypeEnum;
};

export class EntityLocation {
    public bbox?: number[];
    public coordinates?: number[];
    public type?: string;
}

export const DRAWN_POLYGON_SOURCE_ID = 'drawn-polygon-source-id';
export const DRAWN_POLYGON_LAYER_ID = 'drawn-polygon-source-layer';
export const CONNIE_SOURCE_ID = 'connie-source-id';
export const CONNIE_LAYER_ID = 'connie-layer-id';

@Injectable({
    providedIn: 'root',
})
export class MapDrawService extends MapBaseService {
    public currentPolygons: { [key: string]: Polygon } = {};
    public draw!: MapboxDraw;
    public drawPolygonByLayerId: { [key: string]: Polygon[] } = {};
    public readonly hasDrawnPolygon = computed((): boolean => {
        return !!this.drawnPolygonCount();
    });
    public readonly isPolygonDrawSessionActive = signal(false);
    public readonly pendingCenterEntityRequest = signal<PendingCenterEntityRequest | null>(null);
    public readonly pendingEditEntityGeometryRequest = signal<PendingEntityMapRequestBase | null>(null);

    private createFieldInterventionService = inject(CreateFieldInterventionService);
    private readonly drawInstance = signal<MapboxDraw | null>(null);
    private readonly drawnPolygonCount = signal<number>(0);
    private entitiesTableByLayerService = inject(EntitiesTableByLayerService);
    private readonly mapHighlightEntityService = inject(MapHighlightEntityService);
    private readonly selectedPolygonId = signal<string | null>(null);
    private readonly sourcesService = inject(SourcesService);
    private readonly ZOOM_BREAK_POINT = 4;

    public constructor() {
        super();
    }

    public addDrawEvent(): void {
        this.map!.on('draw.create', this.handleCreate.bind(this));
        this.map!.on('draw.update', this.handleUpdate.bind(this));
        this.map!.on('draw.delete', this.handleDelete.bind(this));
        this.map!.on('draw.selectionchange', this.handleSelectionChange.bind(this));
    }

    public centerMapOnEntityAndZoomIn(layerId: string, entityLocation: EntityLocation | null, geom?: Geometry): void {
        const { source } = untracked(() => this.getSourceAndEntityType(layerId, geom));

        const minZoom = this.getSourceVisibleZoom(source);

        this.centerMapOnSpecificEntity(entityLocation, minZoom, source?.maxzoom ?? undefined);
    }

    public centerMapOnSpecificEntity(geomBbox: EntityLocation | null, minZoom?: number, maxZoom?: number): void {
        if (!this.isMapPresent() || !geomBbox) {
            return;
        }

        const geom = geomBbox.bbox as LngLatBoundsLike;
        if (geomBbox.type !== EntityTypeEnum.POINT) {
            this.map.fitBounds(geom);

            return;
        }

        this.map.fitBounds(geom, {
            maxZoom: this.getAdjustedCenterZoom(minZoom, maxZoom),
        });
    }

    public clearDrawnPolygons(): void {
        this.stopDraw();
        this.deleteAllDraw();
    }

    public clearEventListeners(): void {
        if (!this.isMapPresent()) {
            return;
        }

        this.map.off('draw.create', this.handleCreate);
        this.map.off('draw.update', this.handleUpdate);
        this.map.off('draw.delete', this.handleDelete);
    }

    public clearPendingCenterEntityRequest(): void {
        this.pendingCenterEntityRequest.set(null);
    }

    public clearPendingEditEntityGeometryRequest(): void {
        this.pendingEditEntityGeometryRequest.set(null);
    }

    public deleteAllDraw(): void {
        if (!this.isMapPresent() || !this.drawInstance()) {
            this.isPolygonDrawSessionActive.set(false);

            return;
        }

        this.isPolygonDrawSessionActive.set(false);
        this.removeOldPolygons();

        try {
            this.draw.deleteAll();
        } catch {
            /* empty */
        } finally {
            this.refreshDrawnPolygonCount();
        }
    }

    public deleteCurrentDrawPolygon(): void {
        for (const [polygonId, polygon] of Object.entries(this.currentPolygons)) {
            const featureCollection: FeatureCollection = {
                features: [
                    {
                        geometry: polygon,
                        id: polygonId,
                        properties: null,
                        type: Feature.TypeEnum.FEATURE,
                    },
                ],
                type: FeatureCollection.TypeEnum.FEATURE_COLLECTION,
            };

            this.handleDelete(featureCollection);
        }
    }

    public deleteSelectedPolygon(): boolean {
        const polygonId: string | null = this.selectedPolygonId();

        if (!polygonId || !this.draw) {
            return false;
        }

        this.draw.delete(polygonId);
        this.selectedPolygonId.set(null);

        return true;
    }

    public getDrawnMultiPolygon(): MultiPolygon | null {
        const polygons = Object.values(this.currentPolygons);
        if (!polygons.length) {
            return null;
        }

        return {
            coordinates: polygons.map((polygon: Polygon) => polygon.coordinates),
            type: 'MultiPolygon',
        };
    }

    public getSourceAndEntityType(
        layerId: string,
        geom?: Geometry,
    ): { entityType: LayerStyleTypeEnum; source?: MapBaseSource<unknown> } {
        const source = this.sourcesService.getSource(layerId);
        const entityType = this.mapHighlightEntityService.getEntityTypeByEntity(geom);

        return { entityType, source };
    }

    public getSourceVisibleZoom(source?: MapBaseSource<unknown>): number | undefined {
        return source?.minzoom && source.minzoom > this.ZOOM_BREAK_POINT ? source.minzoom : undefined;
    }

    public initializeDrawEngine(): void {
        if (!this.isMapPresent() || this.drawInstance()) {
            return;
        }

        const draw = this.createDrawEngine();
        AssertService.isIControl(draw);

        this.drawInstance.set(draw);
        this.draw = draw;

        draw.onAdd(this.map);

        this.addDrawEvent();
    }

    public removeOldPolygons(): void {
        const source = this.map?.getSource(DRAWN_POLYGON_SOURCE_ID);
        const layer = this.map?.getLayer(DRAWN_POLYGON_LAYER_ID);

        if (!source || !layer) {
            return;
        }

        this.map?.removeLayer(layer.id);
        this.map?.removeSource(source.id);
    }

    public requestCenterEntity(request: PendingCenterEntityRequest): void {
        this.pendingCenterEntityRequest.set(request);
    }

    public requestEditEntityGeometry(request: PendingEntityMapRequestBase): void {
        this.pendingEditEntityGeometryRequest.set(request);
    }

    public resetDraw(): void {
        if (!this.isMapPresent() || !this.drawInstance()) {
            this.isPolygonDrawSessionActive.set(false);
            this.drawInstance.set(null);

            return;
        }

        this.stopDraw();
        this.deleteAllDraw();
        this.destroyDrawEngine();
    }

    public restorePolygonsOnMap(): void {
        if (!this.isMapPresent() || this.map?.getSource(DRAWN_POLYGON_SOURCE_ID)) {
            return;
        }

        this.displayOldPolygons();
    }

    public setIsActivated(source: MapLayerSource): boolean {
        if (!source.minzoom || !source.maxzoom) {
            source.isActivated = true;

            return true;
        }

        return this.getMapZoom() >= source.minzoom && this.getMapZoom() <= source.maxzoom;
    }

    public stopDraw(): void {
        if (!this.isMapPresent() || !this.drawInstance()) {
            this.isPolygonDrawSessionActive.set(false);

            return;
        }

        this.isPolygonDrawSessionActive.set(false);
        try {
            this.draw.changeMode('simple_select');
        } catch {
            return;
        }
    }

    public togglePolygonDraw(isActive: boolean): void {
        if (isActive) {
            this.initializeDrawEngine();

            if (!this.draw) {
                return;
            }

            this.startDrawPolygon();

            return;
        }

        this.stopDraw();
    }

    private createDrawEngine(): MapboxDraw {
        return new MapboxDraw({
            controls: {
                polygon: true,
                trash: true,
            },
            displayControlsDefault: false,
        });
    }

    private destroyDrawEngine(): void {
        if (!this.map) {
            this.drawInstance.set(null);

            return;
        }

        const draw: MapboxDraw | null = this.drawInstance();
        if (!draw) {
            return;
        }

        this.isPolygonDrawSessionActive.set(false);
        this.removeOldPolygons();

        try {
            draw.deleteAll();
        } catch {
            /* empty */
        }

        this.clearEventListeners();

        try {
            AssertService.isIControl(draw);

            draw.onRemove(this.map);
        } catch {
            /* empty */
        }

        this.currentPolygons = {};
        this.drawPolygonByLayerId = {};
        this.drawnPolygonCount.set(0);

        this.drawInstance.set(null);
    }

    private displayOldPolygons(): void {
        if (!this.map || !this.draw) {
            return;
        }

        if (this.map.getSource(DRAWN_POLYGON_SOURCE_ID)) {
            return;
        }

        const featureCollection = this.draw.getAll();

        this.map?.addSource(DRAWN_POLYGON_SOURCE_ID, {
            data: featureCollection,
            type: 'geojson',
        });

        this.map?.addLayer({
            id: DRAWN_POLYGON_LAYER_ID,
            paint: {
                'fill-color': '#088',
                'fill-opacity': 0.5,
            },
            source: DRAWN_POLYGON_SOURCE_ID,
            type: 'fill',
        });
    }

    private handleCreate(featureCollection: FeatureCollection): void {
        this.syncPolygonStateFromFeatureCollection(featureCollection);

        if (!this.isPolygonDrawSessionActive()) {
            return;
        }

        queueMicrotask(() => {
            if (!this.draw) {
                return;
            }

            this.draw.changeMode('draw_polygon');
        });
    }

    private async handleDelete(featureCollection: FeatureCollection): Promise<void> {
        const polygonId = featureCollection.features[0].id;

        if (!polygonId) {
            return;
        }

        delete this.currentPolygons[polygonId];

        if (this.selectedPolygonId() === String(polygonId)) {
            this.selectedPolygonId.set(null);
        }

        const hasCurrentPolygons = !!Object.values(this.currentPolygons).length;

        if (this.createFieldInterventionService.isCreateFieldIntervention() && !hasCurrentPolygons) {
            this.createFieldInterventionService.clearFieldInterventionSelectionFilter();
            this.refreshDrawnPolygonCount();

            return;
        }

        if (this.entitiesTableByLayerService.isFieldInterventionSelectionActive() && !hasCurrentPolygons) {
            await this.entitiesTableByLayerService.deleteZoneFilter();
            this.refreshDrawnPolygonCount();

            return;
        }

        this.createFieldInterventionService.isZoneDelimiterFilterActive.set(true);
        this.entitiesTableByLayerService.isZoneDelimiterFilterActive.set(true);

        this.refreshDrawnPolygonCount();
    }

    private handleSelectionChange(featureCollection: FeatureCollection): void {
        const selectedFeature = featureCollection.features[0];

        if (!selectedFeature?.id) {
            this.selectedPolygonId.set(null);

            return;
        }

        this.selectedPolygonId.set(String(selectedFeature.id));
    }

    private handleUpdate(featureCollection: FeatureCollection): void {
        this.syncPolygonStateFromFeatureCollection(featureCollection);
    }

    private refreshDrawnPolygonCount(): void {
        const fallbackCount = Object.keys(this.currentPolygons).length;

        if (!this.draw) {
            this.drawnPolygonCount.set(fallbackCount);

            return;
        }

        try {
            const featureCollection = this.draw.getAll();
            this.drawnPolygonCount.set(featureCollection.features.length);
        } catch {
            this.drawnPolygonCount.set(fallbackCount);
        }
    }

    private setDrawPolygonById(featureCollection: FeatureCollection): void {
        const polygon = featureCollection.features[0].geometry;

        AssertService.assertPolygonType(polygon);

        if (!featureCollection.features[0].id) {
            return;
        }

        this.currentPolygons[featureCollection.features[0].id] = polygon;
    }

    private startDrawPolygon(): void {
        if (!this.draw) {
            return;
        }

        this.displayOldPolygons();
        this.isPolygonDrawSessionActive.set(true);
        this.draw.changeMode('draw_polygon');
    }

    private syncPolygonStateFromFeatureCollection(featureCollection: FeatureCollection): void {
        this.setDrawPolygonById(featureCollection);
        this.refreshDrawnPolygonCount();
    }
}
